import * as THREE from 'three'
import { PLYLoader } from 'three/addons/loaders/PLYLoader.js'
import { MeshAssetLoadError, MeshLoadCancelledError } from '@/plugins/mesh/errors'
import { createMeshLoadResult } from '@/plugins/mesh/loaders/createMeshLoadResult'
import {
  createMeshProgressReporter,
  describeMeshSource,
  getMeshSourceExtension,
  loadMeshSourceData,
} from '@/plugins/mesh/loaders/loaderUtils'
import {
  validatePlyMeshGeometry,
  validatePlyMeshHeader,
} from '@/plugins/mesh/loaders/plyMeshValidation'
import { MeshPrimitiveNormalizer } from '@/plugins/mesh/MeshPrimitiveNormalizer'
import { MeshResourceTracker } from '@/plugins/mesh/MeshResourceTracker'
import type {
  MeshDefaultMaterialFactory,
  MeshLoadRequest,
  MeshLoadResult,
} from '@/plugins/mesh/types'
import { getLoggerManager } from '@/shared/logging'
import type { AssetLoader, LoadContext } from '@/shared/types/assets'

const log = getLoggerManager().getLogger('mesh:loader')

/** PlyMeshLoader 配置。 */
export interface PlyMeshLoaderOptions {
  defaultMaterial?: MeshDefaultMaterialFactory
  generateNormals?: boolean
}

/** 使用 Three.js PLYLoader 的静态网格格式适配器。 */
export class PlyMeshLoader implements AssetLoader<MeshLoadRequest, MeshLoadResult> {
  readonly format = 'ply'
  readonly detectPriority = 10

  private readonly defaultMaterial?: MeshDefaultMaterialFactory
  private readonly generateNormals: boolean
  private readonly normalizer = new MeshPrimitiveNormalizer()

  constructor(options: PlyMeshLoaderOptions = {}) {
    this.defaultMaterial = options.defaultMaterial
    this.generateNormals = options.generateNormals ?? true
  }

  canLoad(request: MeshLoadRequest): boolean {
    if (request.format) return request.format.toLowerCase() === this.format
    return getMeshSourceExtension(request.url) === this.format
  }

  async load(request: MeshLoadRequest, context: LoadContext): Promise<MeshLoadResult> {
    const source = describeMeshSource(request)
    const signal = context.signal ?? request.signal
    const reportProgress = createMeshProgressReporter(request, context)
    const startedAt = getLoggerManager().now()
    const resources = new MeshResourceTracker()
    log.debug(`开始加载 PLY，来源: ${source}`)

    try {
      reportProgress(0)
      const data = await loadMeshSourceData(request, context, (progress) =>
        reportProgress(progress * 0.6)
      )
      if (signal?.aborted) throw new MeshLoadCancelledError(source)

      const header = validatePlyMeshHeader(data, source)
      const geometry = resources.trackGeometry(new PLYLoader().parse(data))
      validatePlyMeshGeometry(geometry, header, source)
      reportProgress(0.75)
      if (!geometry.getAttribute('normal') && (request.generateNormals ?? this.generateNormals)) {
        geometry.computeVertexNormals()
      }

      const hasVertexColors = geometry.hasAttribute('color')
      const material = resources.trackMaterial(
        this.defaultMaterial?.({ request, hasVertexColors }) ??
          new THREE.MeshStandardMaterial({
            color: 0xb8b8b8,
            roughness: 0.8,
            metalness: 0,
            vertexColors: hasVertexColors,
          })
      )
      const sourceMesh = new THREE.Mesh(geometry, material)
      sourceMesh.name = request.name ?? request.url ?? 'ply'
      const normalized = this.normalizer.normalize(sourceMesh, resources)
      reportProgress(0.95)
      if (signal?.aborted) throw new MeshLoadCancelledError(source)

      const result = createMeshLoadResult(
        this.format,
        normalized,
        { ignoredAnimations: [], warnings: [] },
        getLoggerManager().now() - startedAt
      )
      reportProgress(1)
      log.debug(`PLY 加载完成，来源: ${source}，Primitive: ${result.report.stats.primitiveCount}`)
      return result
    } catch (error) {
      try {
        resources.dispose()
      } catch {
        // 保留原始加载错误；资源跟踪器已经继续尝试清理全部资源。
      }
      if (error instanceof MeshLoadCancelledError) throw error
      log.error(
        `PLY 加载失败，来源: ${source}，原因: ${
          error instanceof Error ? error.message : String(error)
        }`
      )
      throw new MeshAssetLoadError(this.format, source, error)
    }
  }
}
